import {
  COPY_METADATA_SOURCE,
  COPY_METADATA_TARGET,
} from '@disco/constants/ItemTypes'
import { pick } from '@disco/utils/array'
import {
  getTrackCommentText,
  getTrackMetadataFieldName,
  getWriterSplitText,
} from '@disco/utils/track-metadata'
import cn from 'clsx'
import uniqBy from 'lodash/uniqBy'
import without from 'lodash/without'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'hooks/use-redux'
import type { CopyMetadataFields } from 'types/copy-metadata'
import type {
  Comment,
  FullWriterSplit,
  NativeTag,
  Track,
  TrackId,
} from 'types/track'
import { getFullWriterSplits } from '@disco/state-management/selectors/contacts'
import { getTrack } from '@disco/state-management/selectors/tracks'
import { getVisibleCustomFieldsKeys } from '@disco/state-management/selectors/user'
import { newlines } from 'utils/dom'
import CopyMetadataArtwork from 'components/common/copy-metadata-artwork'
import CopyMetadataField from 'components/common/copy-metadata-field'
import TrackInfo from 'components/common/track-info'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Draggable from 'components/ui/draggable'
import ExpandableList from 'components/ui/expandable-list'
import Flex from 'components/ui/flex'
import { HoverProvider } from 'components/ui/hover-provider'
import TreeItem from 'components/ui/tree-item'
import LoadingIndicator from './loading-indicator'
import styles from './index.module.css'

const METADATA_FIELDS_KEYS = [
  'Title',
  'Artist',
  'Album',
  'Composer',
  'Grouping',
  'Genre',
  'Year',
  'Release Date',
  'BPM',
  'ISRC',
  'TrackNumber',
  'Comment',
]
type Props = {
  disabled?: boolean
  expanded?: boolean
  loading: boolean
  overrides?: Omit<Partial<Track>, 'track_writers'> & {
    track_writers?: FullWriterSplit[]
  }
  selectable?: boolean
  selectedFields: CopyMetadataFields
  trackId: TrackId
  onRemoveClick: (id: TrackId) => void
  onSelect?: (
    arg0:
      | ((arg0: CopyMetadataFields) => CopyMetadataFields)
      | CopyMetadataFields,
  ) => void
}

/**
 * Comprehensive track metadata display and selection component for copy metadata interface
 * Shows expandable track information with selectable metadata fields, artwork, tags, writers, and comments
 * Supports both source (selectable) and target (display) modes with visual diff indicators
 * @param disabled - Whether the track component is disabled
 * @param expanded - Whether the metadata details are expanded by default
 * @param loading - Whether the track is in loading state
 * @param overrides - Override values to display with diff styling
 * @param selectable - Whether this is a source track for selection
 * @param trackId - ID of the track to display
 * @param selectedFields - Currently selected metadata fields
 * @param onRemoveClick - Callback when remove button is clicked
 * @param onSelect - Callback when field selection changes
 */
const CopyMetadataTrack = ({
  disabled,
  expanded = false,
  loading,
  overrides,
  selectable,
  trackId,
  selectedFields = {} as CopyMetadataFields,
  onRemoveClick,
  onSelect,
}: Props) => {
  const {
    artwork,
    comments_indexes,
    metadata_keys,
    native_tags_indexes,
    track_writers_indexes,
  } = selectedFields
  const customFieldsKeys: string[] = useSelector(getVisibleCustomFieldsKeys)
  const track: Track | null | undefined = useSelector(state => {
    return getTrack(state, {
      id: trackId,
    })
  })
  const writerSplits: FullWriterSplit[] = useSelector(state => {
    return getFullWriterSplits(state, {
      id: trackId,
    })
  })
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  useEffect(() => {
    setIsExpanded(expanded)
  }, [expanded])
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'Metadata',
  ])

  const getVisibleMetadata = (field: string) =>
    (overrides?.metadata && overrides?.metadata[field]) ??
    track?.metadata[field] ??
    ''

  const visibleMetadata: string[] =
    METADATA_FIELDS_KEYS.filter(getVisibleMetadata)
  const metadata: string[] = selectable ? METADATA_FIELDS_KEYS : visibleMetadata
  const lyrics: string = getVisibleMetadata('Lyrics')
  const writers: FullWriterSplit[] =
    overrides?.track_writers && overrides.track_writers.length > 0
      ? overrides.track_writers
      : writerSplits
  const tags: NativeTag[] = uniqBy(
    [...(track?.native_tags ?? []), ...(overrides?.native_tags ?? [])],
    'id',
  )
  const comments: Comment[] = [
    ...(track?.comments ?? []),
    ...(overrides?.comments ?? []),
  ]
  const visibleCustomFields: string[] =
    customFieldsKeys.filter(getVisibleMetadata)
  const customFields: string[] = selectable
    ? customFieldsKeys
    : visibleCustomFields
  const handleExpand = useCallback(() => {
    setIsExpanded(state => !state)
  }, [])
  const handleRemove = useCallback(() => {
    onRemoveClick(trackId)
  }, [onRemoveClick, trackId])
  const handleSelect = useCallback(
    (data: Partial<CopyMetadataFields>) => {
      if (onSelect) {
        onSelect((state: CopyMetadataFields) => {
          const update = Object.keys(data).reduce<CopyMetadataFields>(
            (accumulator: CopyMetadataFields, key: string) => ({
              ...accumulator,
              [key]:
                typeof data[key] === 'object'
                  ? { ...state[key], ...data[key] }
                  : data[key],
            }),
            {} as CopyMetadataFields,
          )
          return { ...state, ...update }
        })
      }
    },
    [onSelect],
  )
  const toggleSection = useCallback((key: string) => {
    setExpandedSections((state: string[]) =>
      state.includes(key) ? without(state, key) : [...state, key],
    )
  }, [])
  if (!track) return null

  const renderMetadata = () => (
    <div className={styles.copyMetadataTrackMetadata}>
      <CopyMetadataField
        caption="Metadata"
        data={{
          artwork,
          metadata_keys: pick(metadata_keys, METADATA_FIELDS_KEYS),
        }}
        depth={0}
        expanded={expandedSections.includes('Metadata')}
        id="Metadata"
        onExpand={toggleSection}
        onSelect={onSelect ? handleSelect : undefined}
      />
      {expandedSections.includes('Metadata') && (
        <ExpandableList
          limit={5}
          renderButton={(button: React.ReactNode) => (
            <TreeItem caption={button} depth={1} />
          )}
        >
          {(selectable || overrides?.artwork || track.artwork) && (
            <CopyMetadataField
              caption={renderArtworkField()}
              data={{
                artwork,
              }}
              depth={1}
              disabled={!track.artwork && !overrides?.artwork}
              onSelect={onSelect ? handleSelect : undefined}
            />
          )}
          {metadata.map((field: string) => (
            <CopyMetadataField
              key={field}
              caption={renderMetadataField(field)}
              data={{
                metadata_keys: pick(metadata_keys, [field]),
              }}
              depth={1}
              disabled={
                selectable ? !track.metadata[field] : !metadata.includes(field)
              }
              onSelect={onSelect ? handleSelect : undefined}
            />
          ))}
        </ExpandableList>
      )}

      {(selectable || lyrics) && (
        <>
          <CopyMetadataField
            caption="Lyrics"
            depth={0}
            disabled={!lyrics}
            expanded={expandedSections.includes('Lyrics')}
            data={{
              metadata_keys: pick(metadata_keys, ['Lyrics']),
            }}
            id="Lyrics"
            onExpand={toggleSection}
            onSelect={onSelect ? handleSelect : undefined}
          />
          {expandedSections.includes('Lyrics') && (
            <CopyMetadataField
              caption={
                <ExpandableList limit={5}>{renderLyrics()}</ExpandableList>
              }
              depth={1}
            />
          )}
        </>
      )}

      {(selectable ||
        getVisibleMetadata('ProNumber') ||
        writers.length > 0) && (
        <>
          <CopyMetadataField
            caption="Writers"
            data={{
              metadata_keys: pick(metadata_keys, ['ProNumber']),
              track_writers_indexes,
            }}
            depth={0}
            disabled={writers.length === 0 && !getVisibleMetadata('ProNumber')}
            expanded={expandedSections.includes('Writers')}
            id="Writers"
            onExpand={toggleSection}
            onSelect={onSelect ? handleSelect : undefined}
          />
          {expandedSections.includes('Writers') && (
            <>
              {writers.length > 0 && (
                <CopyMetadataField
                  caption={renderWriters()}
                  data={{
                    track_writers_indexes,
                  }}
                  depth={1}
                  onSelect={onSelect ? handleSelect : undefined}
                />
              )}
              {(selectable || getVisibleMetadata('ProNumber')) && (
                <CopyMetadataField
                  caption={renderMetadataField('ProNumber')}
                  data={{
                    metadata_keys: pick(metadata_keys, ['ProNumber']),
                  }}
                  depth={1}
                  disabled={!getVisibleMetadata('ProNumber')}
                  onSelect={onSelect ? handleSelect : undefined}
                />
              )}
            </>
          )}
        </>
      )}

      {(selectable || tags.length > 0) && (
        <>
          <CopyMetadataField
            caption="Tags"
            data={{
              native_tags_indexes,
            }}
            depth={0}
            disabled={tags.length === 0}
            expanded={expandedSections.includes('Tags')}
            id="Tags"
            onExpand={toggleSection}
            onSelect={onSelect ? handleSelect : undefined}
          />
          {expandedSections.includes('Tags') &&
            tags.map((tag: NativeTag, index: number) => (
              <CopyMetadataField
                key={tag.id}
                caption={renderTag(tag)}
                data={{
                  native_tags_indexes: pick(native_tags_indexes, [
                    String(index),
                  ]),
                }}
                depth={1}
                onSelect={onSelect ? handleSelect : undefined}
              />
            ))}
        </>
      )}

      {(selectable || customFields.length > 0) && (
        <>
          <CopyMetadataField
            caption="Custom fields"
            data={{
              metadata_keys: pick(metadata_keys, [...customFields]),
            }}
            depth={0}
            disabled={visibleCustomFields.length === 0}
            expanded={expandedSections.includes('Custom fields')}
            id="Custom fields"
            onExpand={toggleSection}
            onSelect={onSelect ? handleSelect : undefined}
          />
          {expandedSections.includes('Custom fields') &&
            customFields.map((field: string) => (
              <CopyMetadataField
                key={field}
                caption={renderMetadataField(field)}
                data={{
                  metadata_keys: pick(metadata_keys, [field]),
                }}
                depth={1}
                disabled={
                  selectable
                    ? !track.metadata[field]
                    : !customFields.includes(field)
                }
                onSelect={onSelect ? handleSelect : undefined}
              />
            ))}
        </>
      )}

      {(selectable || comments.length > 0) && (
        <>
          <CopyMetadataField
            caption="Notes"
            data={{
              comments_indexes,
            }}
            depth={0}
            disabled={comments.length === 0}
            expanded={expandedSections.includes('Notes')}
            id="Notes"
            onExpand={toggleSection}
            onSelect={onSelect ? handleSelect : undefined}
          />
          {expandedSections.includes('Notes') &&
            comments.map((comment: Comment, index: number) => (
              <CopyMetadataField
                key={index}
                caption={renderComment(comment)}
                data={{
                  comments_indexes: pick(comments_indexes, [String(index)]),
                }}
                depth={1}
                onSelect={onSelect ? handleSelect : undefined}
              />
            ))}
        </>
      )}
    </div>
  )

  const renderArtworkField = () => {
    return (
      <>
        Artwork:{' '}
        {selectable ? (
          <CopyMetadataArtwork artwork={track.artwork} />
        ) : (
          <>
            {!track.artwork && <CopyMetadataArtwork artwork={null} />}
            <CopyMetadataArtwork
              artwork={overrides?.artwork ?? track.artwork}
            />
          </>
        )}
      </>
    )
  }

  const renderMetadataField = (field: string) => {
    const getValue = (value: string | string[]): string =>
      Array.isArray(value) ? value.join(', ') : value

    const hasOverrides: boolean = Boolean(overrides?.metadata?.[field])
    return (
      <>
        {getTrackMetadataFieldName(field)}:{' '}
        {selectable || !hasOverrides ? (
          getValue(track.metadata[field])
        ) : (
          <>
            {track.metadata[field] && (
              <del>{getValue(track.metadata[field])}</del>
            )}{' '}
            {overrides?.metadata?.[field] && (
              <ins>{getValue(overrides?.metadata[field])}</ins>
            )}
          </>
        )}
      </>
    )
  }

  const renderLyrics = () => {
    if (!lyrics) return null
    const content = newlines(lyrics)
    return (
      <>
        {overrides?.metadata?.['Lyrics'] ? (
          <>
            {!track?.metadata['Lyrics'] && <del>blank</del>}
            <ins>{content}</ins>
          </>
        ) : (
          content
        )}
      </>
    )
  }

  const renderWriters = () => (
    <>
      {writerSplits.length === 0 && <del>blank</del>}
      {writers.map((writer: FullWriterSplit, index: number) => (
        <div key={index}>
          {writer.track !== trackId ? (
            <ins>{getWriterSplitText(writer)}</ins>
          ) : (
            getWriterSplitText(writer)
          )}
        </div>
      ))}
    </>
  )

  const renderTag = (tag: NativeTag) => {
    return overrides?.native_tags &&
      !track?.native_tags?.some(({ id }) => tag.id === id) ? (
      <ins>{tag.name}</ins>
    ) : (
      tag.name
    )
  }

  const renderComment = (comment: Comment) => {
    return comment.track_id !== trackId ? (
      <ins>{getTrackCommentText(comment)}</ins>
    ) : (
      getTrackCommentText(comment)
    )
  }

  return (
    <Draggable
      id={track.id}
      type={selectable ? COPY_METADATA_SOURCE : COPY_METADATA_TARGET}
    >
      <div
        className={cn(styles.copyMetadataTrack, {
          [styles.copyMetadataTrackDisabled]: disabled,
        })}
      >
        <HoverProvider>
          <Flex spacing="medium">
            <Flex.Content>
              <TrackInfo location="explorer" track={track} />
            </Flex.Content>
            <ButtonContainer>
              <Button
                appearance="transparent"
                caption="Remove"
                compact
                onClick={handleRemove}
              />
              {loading ? (
                <LoadingIndicator size="small" />
              ) : (
                <Button
                  appearance="icon"
                  compact
                  icon={isExpanded ? 'arrow-down' : 'arrow-right'}
                  onClick={handleExpand}
                />
              )}
            </ButtonContainer>
          </Flex>
        </HoverProvider>
        {!loading && isExpanded && renderMetadata()}
      </div>
    </Draggable>
  )
}

export default React.memo<Props>(CopyMetadataTrack)
